import { HelpCircle, LayoutList, ListFilter, PanelLeftClose, Rows3, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { TriageStatus } from "@/hooks/use-baseline";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { Stakeholder } from "@/hooks/use-stakeholders";
import { useStakeholders } from "@/hooks/use-stakeholders";
import type { TriageFilters, TriageItemRow } from "@/hooks/use-triage";
import { useTriageItems } from "@/hooks/use-triage";
import { cn } from "@/lib/utils";
import { ConfidenceDots, StatusBadge } from "./triage-badge";
import { TRIAGE_DOT } from "./triage-tones";
import { TYPE_ORDER, TYPES } from "./triage-types";

const STATUS_FILTERS: ReadonlyArray<{ id: TriageStatus | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "in-review", label: "Review" },
  { id: "in-scope", label: "In scope" },
  { id: "out-of-scope", label: "Out of scope" },
  { id: "deferred", label: "Deferred" },
];

const TYPE_PILL_CLASSES =
  "inline-flex h-5 min-w-5 px-1 items-center justify-center rounded text-[10px] font-mono font-semibold bg-secondary text-foreground/70 shrink-0";

interface ReviewQueueProps {
  solutionId: string;
  filters: TriageFilters;
  onFilterChange: (next: TriageFilters) => void;
  selectedItemId: string | null;
  onSelectItem: (item: TriageItemRow) => void;
  onCollapse?: () => void;
  view: "row" | "tile";
  onViewChange: (v: "row" | "tile") => void;
}

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;

export function ReviewQueue({
  solutionId,
  filters,
  onFilterChange,
  selectedItemId,
  onSelectItem,
  onCollapse,
  view,
  onViewChange,
}: ReviewQueueProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 250);
  const stakeholdersQuery = useStakeholders(solutionId);

  useEffect(() => {
    if (debouncedSearch === (filters.search ?? "")) return;
    onFilterChange({ ...filters, search: debouncedSearch || undefined });
  }, [debouncedSearch, filters, onFilterChange]);

  const query = useTriageItems(solutionId, filters);
  const items = query.data?.data ?? [];
  const decided = items.filter((i) => i.decision != null).length;

  const activeCount =
    (filters.status ? 1 : 0) + (filters.type ? 1 : 0) + (filters.speakerId ? 1 : 0);
  const summary = filterSummary(filters, stakeholdersQuery.data ?? []);

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-[var(--color-border)] p-3 space-y-2.5 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            Queue
          </span>
          <span className="text-[11px] text-muted-foreground tabular-nums">{items.length}</span>
          {onCollapse && (
            <button
              type="button"
              onClick={onCollapse}
              className="ml-auto inline-flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
              title="Collapse queue"
            >
              <PanelLeftClose className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <FilterPopover
            filters={filters}
            onChange={onFilterChange}
            stakeholders={stakeholdersQuery.data ?? []}
            summary={summary}
            activeCount={activeCount}
          />
          <TypeLegendPopover />
          <ViewToggle value={view} onChange={onViewChange} />
        </div>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search items…"
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {query.isLoading && (
          <ul className="p-2 space-y-2">
            {SKELETON_KEYS.map((k) => (
              <Skeleton
                key={k}
                className={cn("rounded-md", view === "row" ? "h-[34px]" : "h-[88px]")}
              />
            ))}
          </ul>
        )}

        {query.isError && (
          <div className="p-6 text-center text-sm text-destructive">Failed to load items.</div>
        )}

        {query.data && items.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No items match these filters.
          </div>
        )}

        {items.length > 0 && view === "row" && (
          <RowsView items={items} selectedItemId={selectedItemId} onSelect={onSelectItem} />
        )}
        {items.length > 0 && view === "tile" && (
          <TilesView items={items} selectedItemId={selectedItemId} onSelect={onSelectItem} />
        )}
      </div>

      {items.length > 0 && (
        <footer className="border-t border-[var(--color-border)] p-2.5 text-[11px] text-muted-foreground tabular-nums shrink-0">
          {decided} of {items.length} decided
        </footer>
      )}
    </div>
  );
}

// ── Filter popover ────────────────────────────────────────────────

function FilterPopover({
  filters,
  onChange,
  stakeholders,
  summary,
  activeCount,
}: {
  filters: TriageFilters;
  onChange: (next: TriageFilters) => void;
  stakeholders: Stakeholder[];
  summary: string;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);

  function clearAll() {
    onChange({ ...filters, status: undefined, type: undefined, speakerId: undefined });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 gap-2 text-xs flex-1 justify-start",
            activeCount > 0 && "border-nymbl-blue",
          )}
        >
          <ListFilter className="h-3.5 w-3.5" />
          <span className="truncate">{summary}</span>
          {activeCount > 0 && (
            <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-nymbl-blue text-white text-[10px] font-semibold px-1.5">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="p-3 border-b border-[var(--color-border)] flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            Filters
          </div>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
              Clear all
            </button>
          )}
        </div>

        <FilterSection label="Status">
          <div className="flex flex-wrap gap-1">
            {STATUS_FILTERS.map((f) => {
              const active = (f.id === "all" && !filters.status) || f.id === filters.status;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() =>
                    onChange({
                      ...filters,
                      status: f.id === "all" ? undefined : (f.id as TriageStatus),
                    })
                  }
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                    active
                      ? "bg-nymbl-navy text-white"
                      : "bg-muted text-foreground/70 hover:bg-muted/80",
                  )}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </FilterSection>

        <FilterSection label="Type">
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => onChange({ ...filters, type: undefined })}
              className={cn(
                "rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                !filters.type
                  ? "bg-nymbl-navy text-white"
                  : "bg-muted text-foreground/70 hover:bg-muted/80",
              )}
            >
              Any
            </button>
            {TYPE_ORDER.map((id) => {
              const meta = TYPES[id];
              const active = filters.type === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onChange({ ...filters, type: id })}
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                    active
                      ? "bg-nymbl-navy text-white"
                      : "bg-muted text-foreground/70 hover:bg-muted/80",
                  )}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
        </FilterSection>

        {stakeholders.length > 0 && (
          <FilterSection label="Speaker">
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => onChange({ ...filters, speakerId: undefined })}
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                  !filters.speakerId
                    ? "bg-nymbl-navy text-white"
                    : "bg-muted text-foreground/70 hover:bg-muted/80",
                )}
              >
                Any
              </button>
              {stakeholders.map((s) => {
                const active = filters.speakerId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onChange({ ...filters, speakerId: s.id })}
                    title={`${s.name} — ${s.role}`}
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors inline-flex items-center gap-1",
                      active
                        ? "bg-nymbl-navy text-white"
                        : "bg-muted text-foreground/70 hover:bg-muted/80",
                    )}
                  >
                    <span className="text-[9px] font-mono">{s.initials}</span>
                    {s.name.split(" ")[0]}
                  </button>
                );
              })}
            </div>
          </FilterSection>
        )}
      </PopoverContent>
    </Popover>
  );
}

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="p-3 border-b border-[var(--color-border)] last:border-b-0">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

function ViewToggle({
  value,
  onChange,
}: {
  value: "row" | "tile";
  onChange: (v: "row" | "tile") => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-[var(--color-border)] bg-card p-0.5 h-8">
      <button
        type="button"
        onClick={() => onChange("row")}
        title="Compact rows"
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-[5px] transition-colors",
          value === "row"
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <Rows3 className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange("tile")}
        title="Tile cards"
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-[5px] transition-colors",
          value === "tile"
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <LayoutList className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Row vs Tile renderers ─────────────────────────────────────────

function RowsView({
  items,
  selectedItemId,
  onSelect,
}: {
  items: TriageItemRow[];
  selectedItemId: string | null;
  onSelect: (item: TriageItemRow) => void;
}) {
  return (
    <ul>
      {items.map((item) => {
        const type = TYPES[item.type];
        const isActive = item.id === selectedItemId;
        return (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onSelect(item)}
              className={cn(
                "w-full text-left flex items-center gap-2 px-3 h-[34px] border-b border-[var(--color-border)] border-l-2 transition-colors",
                isActive
                  ? "bg-nymbl-sky border-l-nymbl-blue"
                  : "border-l-transparent hover:bg-muted/40",
              )}
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full shrink-0",
                  TRIAGE_DOT[item.decision ? item.effectiveStatus : "pending"],
                )}
                aria-hidden
                title={item.decision ? item.effectiveStatus : "No decision yet"}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={TYPE_PILL_CLASSES}>{type.letter}</span>
                </TooltipTrigger>
                <TooltipContent side="top">{type.label}</TooltipContent>
              </Tooltip>
              <span className="font-mono text-[11px] tabular-nums text-muted-foreground shrink-0">
                {item.externalKey ?? item.id.slice(0, 6)}
              </span>
              <span className="text-[12.5px] text-foreground/85 truncate flex-1">
                {item.extracted}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function TilesView({
  items,
  selectedItemId,
  onSelect,
}: {
  items: TriageItemRow[];
  selectedItemId: string | null;
  onSelect: (item: TriageItemRow) => void;
}) {
  return (
    <ul>
      {items.map((item) => {
        const type = TYPES[item.type];
        const isActive = item.id === selectedItemId;
        return (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onSelect(item)}
              className={cn(
                "w-full text-left px-3 py-2 border-b border-[var(--color-border)] border-l-2 transition-colors",
                isActive
                  ? "bg-nymbl-sky border-l-nymbl-blue"
                  : "border-l-transparent hover:bg-muted/40",
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-[11px] text-muted-foreground tabular-nums shrink-0">
                  {item.externalKey ?? item.id.slice(0, 6)}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-secondary text-foreground/70">
                      {type.label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">{type.description}</TooltipContent>
                </Tooltip>
                <ConfidenceDots confidence={item.confidence} />
                <span className="ml-auto">
                  <StatusBadge status={item.decision ? item.effectiveStatus : "pending"} />
                </span>
              </div>
              <p className="text-[13px] leading-snug text-foreground/85 line-clamp-2">
                {item.extracted}
              </p>
              {item.speaker && (
                <div className="mt-1 text-[11px] text-muted-foreground truncate">
                  <span
                    className={cn(
                      "inline-block h-2 w-2 rounded-full mr-1.5 align-middle",
                      item.speaker.org === "client" ? "bg-nymbl-sky" : "bg-nymbl-navy",
                    )}
                    aria-hidden
                  />
                  {item.speaker.name} ·{" "}
                  {new Date(item.sourceDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ── Helpers ───────────────────────────────────────────────────────

function filterSummary(filters: TriageFilters, stakeholders: Stakeholder[]): string {
  const parts: string[] = [];
  if (filters.status) {
    parts.push(STATUS_FILTERS.find((f) => f.id === filters.status)?.label ?? filters.status);
  }
  if (filters.type) {
    parts.push(TYPES[filters.type].label);
  }
  if (filters.speakerId) {
    const s = stakeholders.find((x) => x.id === filters.speakerId);
    if (s) parts.push(s.name.split(" ")[0]);
  }
  return parts.length === 0 ? "All items" : parts.join(" · ");
}

// ── Legend popover ────────────────────────────────────────────────

function TypeLegendPopover() {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0 shrink-0"
          title="What do these type pills mean?"
          aria-label="Show item-type legend"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <div className="p-3 border-b border-[var(--color-border)]">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            Item types
          </div>
        </div>
        <ul className="p-2 space-y-1">
          {TYPE_ORDER.map((id) => {
            const meta = TYPES[id];
            return (
              <li key={id} className="flex items-start gap-2.5 px-1 py-1">
                <span className={cn(TYPE_PILL_CLASSES, "mt-0.5")}>{meta.letter}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-foreground leading-tight">
                    {meta.label}
                  </div>
                  <div className="text-[11px] text-muted-foreground leading-tight">
                    {meta.description}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
